'use client';
import React, { useEffect, useState } from 'react';
import { MediaCardItem } from './MediaCarousel';
import styles from './RandomFeature.module.css';

const DEFAULT_THUMB = '/imgs/surface-waves/SURFACEWAVE/SURFACEWAVE.jpg';

// Homepage "discover something" panel. The build hands us a pool; we pick one
// at random on the client so every visit (and every "Shuffle") surfaces a
// different page / PDF / video from across the site.
export const RandomFeature: React.FC<{ pool: MediaCardItem[]; heading?: string }> = ({
  pool,
  heading = 'Discover something',
}) => {
  // Server + first client render show pool[0] (stable, no hydration mismatch);
  // after mount we jump to a random item so each visit differs.
  const [idx, setIdx] = useState<number | null>(pool && pool.length ? 0 : null);
  useEffect(() => {
    if (pool && pool.length > 1) setIdx(Math.floor(Math.random() * pool.length));
  }, [pool]);

  if (!pool || pool.length === 0 || idx === null) return null;
  const it = pool[idx];
  const shuffle = () => {
    if (pool.length < 2) return;
    let n = idx;
    while (n === idx) n = Math.floor(Math.random() * pool.length);
    setIdx(n);
  };

  return (
    <section className={styles.feature} aria-label={heading}>
      <div className={styles.head}>
        <h2 className={styles.heading}>{heading}</h2>
        <button type="button" className={styles.shuffle} onClick={shuffle}>↻ Shuffle</button>
      </div>
      <a
        className={styles.card}
        href={it.url}
        target={it.kind === 'page' ? '_self' : '_blank'}
        rel="noopener noreferrer"
        title={it.title}
      >
        <div className={styles.thumbWrap}>
          <img
            className={styles.thumb}
            src={it.thumb || DEFAULT_THUMB}
            alt={it.title}
            onError={(e) => {
              const img = e.currentTarget;
              if (img.dataset.fb === '1') { img.style.display = 'none'; return; }
              img.dataset.fb = '1';
              img.src = DEFAULT_THUMB;
            }}
          />
          <span className={`${styles.badge} ${styles[it.kind]}`}>
            {it.kind === 'video' ? '▶ video' : it.kind === 'pdf' ? 'PDF' : 'page'}
          </span>
        </div>
        <div className={styles.body}>
          <div className={styles.title}>{it.title}</div>
          {it.meta ? <div className={styles.meta}>{it.meta}</div> : null}
        </div>
      </a>
    </section>
  );
};

export default RandomFeature;
